<template>
  <div v-if="klassesLoaded" class="admin-courses">
    <div class="top-form">
      <div class="course-select">
        <h3>{{ $store.state.user.langEng ? 'Courses for class' : 'Kurser för klass' }}</h3>
        <admin-select
          class="klass-select"
          :data="selectData"
          :initial="selectedKlass"
          v-model="selectedKlass"
        />
      </div>
      <router-link to="/admin/courses/edit" class="btn-flat" id="newCourseButton">
        {{ $store.state.user.langEng ? 'Add new course' : 'Lägg till en ny kurs' }}
      </router-link>
    </div>
    <div class="course-table">
      <admin-table
        :columns="columns"
        :buttons="showButtons"
        :data="klassCourses"
        @edit="editCourse"
        @deactivate="deactivateCourse"
        @row-click="rowClicked"
      />
    </div>
  </div>
  <div v-else class="preloader-wrapper big active">
    <div class="spinner-layer spinner-blue-only">
      <div class="circle-clipper left">
        <div class="circle"></div>
      </div><div class="gap-patch">
        <div class="circle"></div>
      </div><div class="circle-clipper right">
        <div class="circle"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { Vue, Component, Watch } from 'vue-property-decorator';
import AdminTable from '@/components/Admin/AdminTable.vue';
import AdminSelect from '@/components/Admin/AdminSelect.vue';
import { Api, Rest, getWeek } from '../utils';

@Component({
  components: { AdminTable, AdminSelect }
})
export default class AdminCourses extends Vue {
  columns = [
    {
      header: () => this.$store.state.user.langEng ? 'Course' : 'Kurs',
      value: (course) => `<span ${course.inactive && 'style="color: gray"'}>${course.name} (${course.points}p)</span>`
    },
    {
      header: 'Period',
      value: (course) => {
        let start = new Date(course.start)
        let end = new Date(course.end)
        let startWeek = getWeek(start)
        let endWeek = getWeek(end)
        return `
        <span class="course-period"
          ${course.inactive && 'style="color: gray"'}>
          <span>
            (v${startWeek}) ${start.toISOString().slice(0, 10)}
          </span>
          <span>
            (v${endWeek}) ${end.toISOString().slice(0, 10)}
          </span>
        </span>`
      }
    }
  ];

  selectedKlass = null;
  klassesLoaded = false;
  
  klasses = [];
  selectData = [];

  @Watch('selectedKlass')
  onSelectedKlass() {
    this.$store.commit('admin/setSelectedKlass', this.currentKlass && this.currentKlass.slug)
  }

  get isEng() {
    return this.$store.state.user.langEng;
  }

  get currentKlass() {
    return this.klasses[this.selectedKlass];
  }

  get showButtons() {
   return !this.mobile;
  }

  get mobile() {
    return this.$store.state.isMobile;
  }

  get klassCourses() {
    if (this.klassesLoaded) {
      let courses = this.klasses[this.selectedKlass].courses
      // sort by time
      // courses.sort((a, b) => a.start < b.start ? -1 : 1)

      // sort by name
      courses.sort((a, b) => a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1)
      return courses;
    }
    else return [];
  }

  editCourse(course) {
    this.$router.push({
      name: 'adminEditCourse',
      params: { slug: course.slug }
    });
  }

  async deactivateCourse(course) {
    let courseToUpdate = {
      id: course.id,
      inactive: !course.inactive
    }

    let res = await Api.post('admin/set-active/Course', courseToUpdate)
    if(res.id) {
      course.inactive = res.inactive 
    }
  }

  rowClicked(course) {
    if (this.$store.state.isMobile) {
      this.$router.push({
        name: 'adminEditCourse',
        params: { slug: course.slug }
      });
    }
  }

  async fetchKlasses() {
    const klasses = await Rest.get("Klass", {
      sort: { name: "ASC" },
      eager: { courses: true },
    });

    // map them to slug names
    let klassObj = {};
    for (const [, value] of Object.entries(klasses)) {
      klassObj[value.slug] = value;
    }

    return klassObj;
  }

  async created() {
    this.selectedKlass = this.$store.state.admin.selectedKlass || 
                         this.$store.getters.classNameInUrl
    this.klasses = await this.fetchKlasses(); // too slow store load-in so we manually load it here

    setTimeout(() => {
      const elems = document.querySelectorAll('select');
      this.$M.FormSelect.init(elems);
    }, 0);

    this.klassesLoaded = true;

    // map the data to the selects required format [{value: x, text: y}]
    this.selectData = Object.keys(this.klasses).map(k => ({ value: k, text: this.klasses[k].slug }));
  }
}
</script>

<style lang="scss">
.course-period {
    display: grid;
    grid-template-rows: 1fr 1fr;
  }

.admin-courses {
  $btn-color: #585858;

  .top-form {
    padding-top: 3em;
    display: grid;
    grid-template-columns: 3fr 1fr;
    justify-items: left;

    h3 {
      font-size: 1.5em;
      font-weight: bold;
      text-align: end;
    }

    .btn-flat {
      border: solid 1px $btn-color;
      color: $btn-color;
      font-weight: bold;
      text-transform: initial;

      &:hover {
        background-color: lightgray;
      }
    }

    #newCourseButton {
      justify-self: right;
      margin-right: 1.5em;
    }
  }

  .course-select {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 10px;
    align-items: center;

    .klass-select {
      border-radius: 4px;
      border: solid 1px grey;
      padding: 0 1em !important;

    }
  }

  .course-table {
    margin: 2em 0;
    padding: 0 1em;
  }
}

.mobile {
  .admin-courses {
    padding: 0 15px;
  }

  .top-form {
    grid-template: 1fr / 1fr;
    row-gap: 0.5em;
    justify-items: center;
    
    h3 {
      font-size: 1em;
    }

    .course-select {
      grid-template: 1fr / 1fr;
      row-gap: 0.5em; 
      justify-items: left;
      width: 100%;

      .klass-select {
        width: 100%;
      }
    }

    #newCourseButton {
      justify-self: left;
      width: 100%;
      text-align: center;
    }
  }

  .course-table {
    padding: 0 0;
  }
}

</style>
